import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { asMoney, isoDateToMMM } from "../../../utils";
import {
    DigitalBooking,
    useCampaignDeletePanelsMutationMutation,
    useDeleteDigitalBookingMutation,
    useGetCampaignDigitalBookingsQueryQuery,
    useTweakDigitalBookingsMutation
} from "../../../generated/graphql";
import { Delete, ThumbUpSharp } from "@mui/icons-material";
import { Icon } from '@iconify/react';
import { Button, ButtonGroup } from "@mui/material";
import { FieldTableColumnProps } from "../../../components/FieldTable/FieldTableTypes";
import WaitSpinner from "../../../components/wait-spinner";
import { FieldTable } from "../../../components/FieldTable/FieldTable";
import { useToast } from "../../../contexts/toast";
import { useOutletContext } from "react-router-dom";
import { CampaignOutletContextValue } from "../form";
import { useDaybookContext } from "../../../contexts/Daybook/DayBookContext";
import { useUserContext } from "../../../contexts/user-context";
import { pencilIcon } from "../../../utils/icons";

const DigitalBookingPane = () => {
    const { setValue, values, jobRef, updateInitialValues, readOnly, setTab } =
        useOutletContext<CampaignOutletContextValue>();

    const user = useUserContext();
    useEffect(() => {
        setTab("digital-bookings");
    }, []);
    const [selectedRows, setSelectedRows] = useState({});
    const [waiting, setWaiting] = useState(false);
    const [doneRender, setDoneRender] = useState(false);
    const { toast, dismiss } = useToast();
    const daybook = useDaybookContext();
    const { loading, refetch, data } = useGetCampaignDigitalBookingsQueryQuery({
        variables: { ref: jobRef },
        skip: true,
        fetchPolicy: "network-only",
        /*onCompleted: (data) => {
                updateInitialValues(data.campaign.panelBookings, 'panelBookings');
            }*/
    });
    useEffect(() => {
        if (!doneRender && !loading && jobRef) {
            refetch({ ref: jobRef }).then(({ data }) => {
                console.log("data.campaign?.digitalBookings", data.campaign?.digitalBookings)
                if (data?.campaign?.digitalBookings) {
                    updateInitialValues(data.campaign?.digitalBookings, 'digitalBookings');
                }
            });
            setDoneRender(true);
        }
    }, [doneRender, loading, jobRef, refetch]);

    const [deleteMutation] = useDeleteDigitalBookingMutation();
    const [tweakMutation] = useTweakDigitalBookingsMutation();

    const columns: FieldTableColumnProps<DigitalBooking>[] = [
        {
            accessor: "selector",
            noSort: true,
            dataType: "selector",
        },
        {
            header: 'Region',
            accessor: 'site.boroughOrRegion',
            className: 'column-region',
            dataType: 'default'
        },
        {
            header: "Site Name",
            accessor: "site.name",
            dataType: "default",
        },
        {
            header: 'Site Structure',
            accessor: 'site.siteStructure.name',
            dataType: 'default',
        },
        {
            header: "Cost Type",
            accessor: "cost_type",
            dataType: "default",
        },
        {
            header: "Total Days",
            accessor: "days",
            dataType: "default",
        },
        {
            header: "Date Up",
            accessor: "start_date",
            dataType: "date",
            renderer: ({ field }) => {
                return isoDateToMMM(field.value);
            },
        },
        {
            header: "Date Down",
            accessor: "end_date",
            dataType: "date",
            renderer: ({ field }) => {
                return isoDateToMMM(field.value);
            },
        },
        {
            header: "Total Amount",
            accessor: "campaignComponent.unit_value_local",
            dataType: "currency",
        },
        {
            header: 'Pro Photos',
            accessor: 'is_photo_paid',
            dataType: 'booleanCamToggle'
        }
    ];

    const tweakRow = useCallback(async (evt) => {
        if (evt) evt.preventDefault();
        const el = evt.target.closest('button');
        const status = el.getAttribute('data-status');
        if (waiting) return;

        const toExec = [];
        Object.entries(selectedRows).forEach(([k, value]) => {
            if (value) {
                toExec.push({ id: k, status });
            }
        });

        if (!toExec.length) return;

        setWaiting(true);
        toast('Updating digital bookings', { id: 'PANEL_UPDATE' });
        console.log("toExectoExec", toExec)

        try {
            const { data } = await tweakMutation({
                variables: { input: { tweak: toExec } },
            });

            const updatedBookings = data?.tweakDigitalBookings?.updatedDigitalBookings || [];
            const errors = data?.tweakDigitalBookings?.tweakErrors || [];
            
            if (errors?.length) {
                errors.forEach((err) => {
                    toast(err?.message, { variant: 'error' });
                });
            }

            if (updatedBookings?.length) {
                toast('Digital bookings updated successfully', { variant: 'success' });
                updateInitialValues(updatedBookings, 'digitalBookings');
            }

            setWaiting(false);
        } catch (e) {
            toast(`Error: ${e.message}`, { variant: 'error' });
            setWaiting(false);
        }
    }, [waiting, selectedRows, tweakMutation, toast, updateInitialValues]);


    const deleteRow = useCallback(
        async (evt) => {
            if (evt) evt.preventDefault();
            if (waiting) return;
            const toExec = [];
            Object.entries(selectedRows).forEach(([k, value]) => {
                if (value) {
                    toExec.push(k);
                }
            });
            if (!toExec.length) return;
            setWaiting(true);

            try {
                const data = await deleteMutation({
                    variables: {
                        input: {
                            deletes: toExec
                        }
                    }
                });
                refetch();
                let seenError = false;
                data?.data?.deleteDigitalBooking?.deleteDigitalBookingErrors?.forEach(
                    (ajaxError) => {
                        seenError = true;
                        toast(
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: ajaxError?.message ?? "An error has occured",
                                }}
                            />,
                            { variant: "error", id: "PANEL_DELETE1" }
                        );
                    }
                );
                if (!seenError) {
                    toast("Digital booking(s) deleted successfully", {
                        variant: "error",
                        id: "PANEL_DELETE2",
                    });
                }
                const newSelected = {};
                Object.entries(selectedRows).forEach(([k, v]) => {
                    if (toExec.indexOf(k) >= 0) return;
                    newSelected[k] = v;
                });
                setSelectedRows(newSelected);
                setWaiting(false);
            } catch (e) {
                toast(e.message, { variant: "error", id: "PANEL_DELETE3" });
                setWaiting(false);
            }
        },
        [deleteMutation, selectedRows, setValue, waiting]
    );

    if (waiting) return <WaitSpinner variant="overlay" />;

    // if no entries are selected then disable the buttons
    const noSel = !Object.entries(selectedRows).some(([, v]) => v);
    const toExec: string[] = [];
    Object.entries(selectedRows).forEach(([k, value]) => {
        if (value) {
            toExec.push(k);
        }
    });

    const doChange = (evt) => {
        setValue(evt.target.name, evt.target.value);
    };
    const getRowClass = (data) => {
        return "booking-status__bg__" + data?.status?.toLowerCase();
    };

    return (
        <>
            <div className="is-scrollable">
                <FieldTable
                    name="digitalBookings"
                    columns={columns}
                    setSelectedRows={setSelectedRows}
                    selectedRows={selectedRows}
                    hasDelete={false}
                    className="campaigns__panel-bookings"
                    values={values.digitalBookings}
                    loading={loading}
                    stickyFooter={true}
                    setValue={setValue}
                    getRowClass={getRowClass}
                    readOnly={readOnly}
                    footerClassName="campaign__panel-bookings__footer"
                    footer={
                        <>
                            <div className="campaign__panel-bookings__footer__actions">
                                {!readOnly && (
                                    <ButtonGroup size="large">
                                        <Button
                                            disabled={noSel}
                                            variant="contained"
                                            color="warning"
                                            title="Delete bookings"
                                            onClick={deleteRow}
                                        >
                                            <Delete />
                                        </Button>
                                        <Button disabled={noSel} variant="contained" color="success" data-start={0} data-dur={0} data-status="Booked" title="Confirm bookings" onClick={tweakRow}>
                                            <ThumbUpSharp />
                                        </Button>
                                        <Button disabled={noSel} color="info" data-start={0} data-dur={0} data-status="Pencil" title="Revert to pencil booking" onClick={tweakRow}>
                                            <Icon icon={pencilIcon} />
                                        </Button>
                                    </ButtonGroup>
                                )}
                            </div>
                        </>
                    }
                >
                </FieldTable>
            </div>
        </>
    );
};

DigitalBookingPane.propTypes = {
    values: PropTypes.array,
    setValue: PropTypes.func,
};

export default DigitalBookingPane;
